import type { CrowdBed } from '@src/audio/CrowdBed';
import { type Sfx, SfxId } from '@src/audio/Sfx';

export interface VictoryStats {
  score: number;
  kills: number;
  combo: number;
  floor: number;
  rooms: number;
}

export interface VictoryCinematicOptions {
  host: HTMLElement;
  sfx: Sfx;
  crowd: CrowdBed;
  stats?: VictoryStats;
  onComplete: () => void;
}

const ART = '/assets/cinematics/victory';
const SKIP_ARM_MS = 450;
const DEFAULT_MS = 14000;
const TAIL_MS = 900;
const VOICE_DELAY_MS = 560;
const VOICE_GAP_MS = 380;
const STING_DELAY_MS = 80;

const BEATS: ReadonlyArray<{ at: number; kicker: string; title: string; line: string }> = [
  { at: 280, kicker: 'LIVE FROM THE GRAND ARENA', title: 'YOU BEAT THE ARENA', line: 'THE FINAL ARCHMAGE FALLS' },
  { at: 4800, kicker: 'ARCANE BROADCAST NETWORK', title: 'CHAMPION', line: 'ONE ROBE. ONE STAFF. THE TOWER IS QUIET.' },
  { at: 9200, kicker: 'WIZARD ARENA: TOTAL MAYHEM', title: 'THE CROWN IS YOURS', line: 'SIX FLOORS. ONE WIZARD.' },
];

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  parent?: HTMLElement,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  parent?.appendChild(node);
  return node;
}

function formatScore(n: number): string {
  return n.toLocaleString('en-US');
}

/**
 * Broadcast championship finale: full-bleed video, generated sting + VO beats,
 * on-screen lockups, then the existing victory panel. Space / Enter / click skip
 * after a short arm.
 */
export class VictoryCinematic {
  private readonly root: HTMLElement;
  private readonly progressBar: HTMLElement;
  private readonly skipBtn: HTMLButtonElement;
  private readonly kicker: HTMLElement;
  private readonly title: HTMLElement;
  private readonly line: HTMLElement;
  private readonly sfx: Sfx;
  private readonly crowd: CrowdBed;
  private readonly onComplete: () => void;
  private readonly startTime: number;
  private readonly timers = new Set<number>();
  private readonly voiceAudio: HTMLAudioElement[] = [];
  private stingAudio: HTMLAudioElement | null = null;
  private durationMs = DEFAULT_MS;
  private animFrame = 0;
  private completed = false;
  private armed = false;
  private beatIndex = -1;
  private cleanupListeners: () => void = () => {};

  constructor(opts: VictoryCinematicOptions) {
    this.sfx = opts.sfx;
    this.crowd = opts.crowd;
    this.onComplete = opts.onComplete;
    this.startTime = performance.now();

    const root = el('div', 'victory-cine', opts.host);
    this.root = root;

    el('div', 'victory-cine-letterbox top', root);
    el('div', 'victory-cine-letterbox bottom', root);

    const stage = el('div', 'victory-cine-stage', root);
    const still = document.createElement('img');
    still.className = 'victory-cine-still';
    still.src = `${ART}.jpg`;
    still.alt = '';
    still.decoding = 'async';
    still.addEventListener('error', () => still.remove(), { once: true });
    stage.appendChild(still);

    const video = document.createElement('video');
    video.className = 'victory-cine-video';
    video.src = `${ART}.mp4`;
    video.autoplay = true;
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.setAttribute('playsinline', '');
    video.addEventListener('loadedmetadata', () => {
      if (video.duration && isFinite(video.duration) && video.duration > 0) {
        this.durationMs = Math.max(this.durationMs, video.duration * 1000);
      }
    });
    video.addEventListener('canplay', () => {
      video.classList.add('ready');
      video.play().catch(() => {});
    });
    video.addEventListener('error', () => {
      video.remove();
    });
    stage.appendChild(video);

    el('div', 'victory-cine-scan', stage);
    el('div', 'victory-cine-vignette', stage);
    el('div', 'victory-cine-grain', stage);
    el('div', 'victory-cine-flash', stage);
    el('div', 'victory-cine-sparks', stage);

    const chrome = el('div', 'victory-cine-chrome', root);
    const brand = el('div', 'victory-cine-brand', chrome);
    el('span', 'victory-cine-onair', brand, 'ON AIR');
    el('span', 'victory-cine-network', brand, 'ARCANE BROADCAST NETWORK');
    const rec = el('span', 'victory-cine-rec', chrome);
    el('i', '', rec);
    rec.appendChild(document.createTextNode(' LIVE'));
    el('span', 'victory-cine-tag', chrome, 'FINALE');

    const lower = el('div', 'victory-cine-lower', root);
    const lockup = el('div', 'victory-cine-lockup', lower);
    this.kicker = el('p', 'victory-cine-kicker', lockup, BEATS[0].kicker);
    this.title = el('h1', 'victory-cine-title', lockup, BEATS[0].title);
    this.line = el('p', 'victory-cine-line', lockup, BEATS[0].line);

    const stats = opts.stats;
    if (stats) {
      const plate = el('div', 'victory-cine-score', lower);
      el('p', 'victory-cine-score-kicker', plate, 'FINAL TALLY');
      el('p', 'victory-cine-score-value', plate, formatScore(stats.score));
      el(
        'p',
        'victory-cine-score-meta',
        plate,
        `FLOOR ${stats.floor}  //  ${stats.kills} KILLS  //  BEST COMBO ${stats.combo}  //  ROOM ${stats.rooms}`,
      );
    }

    const bottom = el('div', 'victory-cine-bottom', root);
    const track = el('div', 'victory-cine-progress', bottom);
    this.progressBar = el('i', '', track);
    this.skipBtn = el('button', 'victory-cine-skip', bottom);
    this.skipBtn.type = 'button';
    el('span', 'victory-cine-skip-key', this.skipBtn, 'SPACE / ENTER / CLICK');
    el('span', 'victory-cine-skip-label', this.skipBtn, 'SKIP');

    this.skipBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.requestSkip();
    });
    root.addEventListener('click', () => {
      if (this.armed) this.requestSkip();
    });

    this.bindInputs();
    this.preloadAudio();
    this.playBed();
    this.delay(SKIP_ARM_MS, () => {
      this.armed = true;
      this.skipBtn.classList.add('armed');
    });
    this.applyBeat(0);
    this.tick();
  }

  private preloadAudio(): void {
    this.stingAudio = this.loadAudio(`${ART}_sting.mp3`, 0.82, (ms) => {
      this.durationMs = Math.max(this.durationMs, STING_DELAY_MS + ms);
    });
    let voiceCursor = VOICE_DELAY_MS;
    for (let i = 0; i < 3; i++) {
      const node = this.loadAudio(`${ART}_${i}.mp3`, 0.98, (ms) => {
        voiceCursor += ms + VOICE_GAP_MS;
        this.durationMs = Math.max(this.durationMs, voiceCursor);
      });
      if (node) this.voiceAudio.push(node);
    }
  }

  private loadAudio(src: string, volume: number, onDuration: (ms: number) => void): HTMLAudioElement | null {
    try {
      const node = new Audio(src);
      node.preload = 'auto';
      node.volume = volume;
      node.addEventListener('loadedmetadata', () => {
        if (node.duration && isFinite(node.duration) && node.duration > 0) {
          onDuration(node.duration * 1000);
        }
      });
      return node;
    } catch {
      return null;
    }
  }

  private playBed(): void {
    this.sfx.play(SfxId.OnAir, 0.95);
    this.delay(STING_DELAY_MS, () => {
      if (this.completed) return;
      this.sfx.play(SfxId.VictorySting, 1);
      this.stingAudio?.play().catch(() => {});
    });
    this.delay(200, () => {
      if (this.completed) return;
      this.sfx.play(SfxId.OrchestraHit, 0.85);
    });
    this.delay(VOICE_DELAY_MS, () => this.playVoiceChain(0));
    this.delay(VOICE_DELAY_MS + 200, () => {
      if (this.completed) return;
      this.crowd.sting('cheerBig', 0.88, 0, true);
    });
    this.delay(VOICE_DELAY_MS + 850, () => {
      if (this.completed) return;
      this.crowd.sting('applause', 0.62, 0, true);
    });
  }

  private playVoiceChain(index: number): void {
    if (this.completed || index >= this.voiceAudio.length) return;
    const node = this.voiceAudio[index];
    const next = (): void => {
      this.delay(VOICE_GAP_MS, () => this.playVoiceChain(index + 1));
    };
    node.addEventListener('ended', next, { once: true });
    node.addEventListener('error', next, { once: true });
    node.play().catch(next);
  }

  private applyBeat(index: number): void {
    if (index === this.beatIndex || index < 0 || index >= BEATS.length) return;
    this.beatIndex = index;
    const beat = BEATS[index];
    this.kicker.textContent = beat.kicker;
    this.title.textContent = beat.title;
    this.line.textContent = beat.line;
    this.title.classList.remove('punch');
    void this.title.offsetWidth;
    this.title.classList.add('punch');
  }

  private delay(ms: number, fn: () => void): void {
    const id = window.setTimeout(() => {
      this.timers.delete(id);
      if (!this.completed) fn();
    }, ms);
    this.timers.add(id);
  }

  private tick = (): void => {
    if (this.completed) return;
    const elapsed = performance.now() - this.startTime;
    const total = this.durationMs + TAIL_MS;
    this.progressBar.style.transform = `scaleX(${Math.min(1, elapsed / total)})`;
    for (let i = BEATS.length - 1; i >= 0; i--) {
      if (elapsed >= BEATS[i].at) {
        this.applyBeat(i);
        break;
      }
    }
    if (elapsed >= total) {
      this.finish();
      return;
    }
    this.animFrame = requestAnimationFrame(this.tick);
  };

  private bindInputs(): void {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!this.armed || this.completed) return;
      if (e.code === 'Space' || e.code === 'Enter' || e.code === 'Escape' || e.code === 'KeyP') {
        e.preventDefault();
        e.stopPropagation();
        this.requestSkip();
      }
    };

    let pollPad = 0;
    const checkGamepad = () => {
      if (this.completed) return;
      const pads = navigator.getGamepads?.() ?? [];
      for (let i = 0; i < pads.length; i++) {
        const p = pads[i];
        if (!p) continue;
        if (p.buttons[0]?.pressed || p.buttons[1]?.pressed || p.buttons[9]?.pressed) {
          if (this.armed) {
            this.requestSkip();
            return;
          }
        }
      }
      pollPad = requestAnimationFrame(checkGamepad);
    };
    pollPad = requestAnimationFrame(checkGamepad);

    window.addEventListener('keydown', onKeyDown, { capture: true });
    this.cleanupListeners = () => {
      window.removeEventListener('keydown', onKeyDown, { capture: true });
      cancelAnimationFrame(pollPad);
    };
  }

  private requestSkip(): void {
    if (!this.armed || this.completed) return;
    this.finish();
  }

  private stopAudio(node: HTMLAudioElement | null): void {
    if (!node) return;
    try {
      node.pause();
      node.currentTime = 0;
    } catch {
      // audio cleanup
    }
  }

  public finish(): void {
    if (this.completed) return;
    this.completed = true;
    cancelAnimationFrame(this.animFrame);
    for (const id of this.timers) window.clearTimeout(id);
    this.timers.clear();
    this.cleanupListeners();
    this.stopAudio(this.stingAudio);
    this.stingAudio = null;
    for (const node of this.voiceAudio) this.stopAudio(node);
    this.voiceAudio.length = 0;

    this.root.classList.add('leaving');
    window.setTimeout(() => {
      this.root.remove();
      this.onComplete();
    }, 280);
  }
}
